/**
 * TableFooter Component
 *
 * This component renders the footer of a table, specifically handling actions such as viewing data per currency and expanding a summary of currency data.
 * It conditionally displays different footer actions depending on the type of column, including buttons for expanding data or viewing data per currency.
 * It also handles interaction logic such as showing or hiding currency lists, and it listens for clicks outside to close the currency list.
 *
 * Props:
 *  - `column` (string): The name of the column for which the footer is being rendered. This determines the behavior and actions of the footer.
 *  - `isLastColumn` (boolean): A flag indicating whether the column is the last column in the table. It helps in applying border styles.
 *  - `isFirstColumnAfterCCY` (boolean): A flag to identify if the column is the first column after the currency column. Used for styling.
 *
 * State/Refs:
 *  - `shouldExpandCurrencies` (boolean): A state that controls whether the currency list is expanded or collapsed.
 *  - `menuRef` (RefObject<HTMLDivElement>): Reference to the footer cell that contains the button, used for detecting clicks outside to close the currency list.
 *  - `rowDensityClass`: Class related to the row density, derived from the custom `useRowDensity` hook, used for styling rows.
 *
 * Effects/Actions:
 *  - `useCloseOnScrollAndClickOutside`: Hook used to close the currency list if the user clicks or scrolls outside of the footer cell.
 *
 * Logic:
 *  - **Geo Columns**: The component skips rendering for geo columns excluding "last column before CCY" by using the `isGeoColumnExcludingLastBeforeCCY` utility.
 *  - **Last column before CCY**: Displays a "View Per Currency" button which triggers the `handleViewPerCurrency` action.
 *  - **Currency Column**: Displays a button to "Expand Sum" which, when clicked, triggers the `handleExpandSum` action. If there is only one available currency, it fetches stats for expanding the sum.
 *  - **Footer Actions**: The component dynamically assigns a footer action (button) depending on the column type (Last column before CCY or Currency).
 *
 * Example Usage:
 *  <TableFooter
 *    column="CCY"
 *    isLastColumn={false}
 *    isFirstColumnAfterCCY={true}
 *  />
 */

import React, { useCallback, useRef } from 'react';
import { t } from 'i18next';
import { StatsTableColumn } from '../../../../../enum/TableColumn';
import {
  borderStyle,
  getLastColumnBeforeCurrency,
  getTableClass,
  isDeviceColumnExcludingLastBeforeCCY,
  isGeoColumnExcludingLastBeforeCCY,
} from '../../../../../util/table-helper';
import currencyIcon from '../../../../../assets/img/svg/table/currency.svg';
import upArrowIcon from '../../../../../assets/img/svg/table/up-arrow.svg';
import sum from '../../../../../assets/img/svg/buttons/sum.svg';
import Button from '../../Button';
import store from '../../../../../store/store';
import {
  clearCurrencyData,
  fetchStats,
  setShouldViewCurrencies,
  setShouldViewPerCurrency,
} from '../../../../../store/slice/stats-slice';
import { useRowDensity } from '../../../../../hook/useRowDensity';
import { StatsFetchType } from '../../../../../enum/StatsFetchType';
import CurrencyList from './CurrencyList/CurrencyList';
import { useStatsPayload } from '../../../../../hook/useStatsPayload';
import { StatsGroupEnum } from '../../../../../enum/StatsGroupEnum';
import { useAppSelector } from '../../../../../hook/useAppSelector';
import { buildRequestForDeepestGeoLevel } from '../../../../../util/buildRequestForDeepestGeoLevel';
import useDateAndTime from '../../../../../hook/useDateAndTime';
import { useAppDispatch } from '../../../../../hook/useAppDispatch';
import { useOutsideFocus } from '../../../../../hook/useOutsideFocus';
import { usePageVisibleColumns } from '../../../../../hook/usePageVisibleColumns';

interface TableFooterProps {
  column: string;
  isLastColumn: boolean;
  isFirstColumnAfterCCY: boolean;
}

const TableFooter: React.FC<TableFooterProps> = ({ column, isLastColumn, isFirstColumnAfterCCY }) => {
  const statsPayload = useStatsPayload();
  const { visibleColumns } = usePageVisibleColumns();

  const isMobile = useAppSelector((state) => state.configSlice.isMobile);
  const disableFooterSum = useAppSelector((state) => state.stats.disableFooterSum);
  const filters = useAppSelector((state) => state.globalFilters);
  const dispatch = useAppDispatch();

  const shouldViewCurrencies = useAppSelector((state) => state.stats.shouldViewCurrencies);

  const menuRef = useRef<HTMLDivElement>(null);
  const { rowDensityClass } = useRowDensity();

  const { startDate, endDate } = useDateAndTime();

  const handleCloseCurrencies = useCallback(() => {
    dispatch(setShouldViewCurrencies(false));
  }, [dispatch]);

  useOutsideFocus(shouldViewCurrencies, handleCloseCurrencies, menuRef);

  // Skip irrelevant geo/device columns
  if (
    isGeoColumnExcludingLastBeforeCCY(column, visibleColumns) ||
    isDeviceColumnExcludingLastBeforeCCY(column, visibleColumns)
  ) {
    return (
      <div className={`table_footer ${getTableClass(column, 'footer', visibleColumns, isMobile)}`}>
        <div className={`table_row ${getTableClass(column, 'footer_row', visibleColumns, isMobile)}`}>
          <div className={`table_cell ${rowDensityClass}`} />
        </div>
      </div>
    );
  }

  const handleViewPerCurrency = async () => {
    // Clear existing currency data and fetch stats for the "View Per Currency" action
    store.dispatch(clearCurrencyData());
    await store.dispatch(
      fetchStats({
        type: StatsFetchType.VIEW_PER_CURRENCY,
        request: {
          ...statsPayload,
          ...buildRequestForDeepestGeoLevel(filters),
          statisticBy: 1,
          Date: { StartDate: startDate, EndDate: endDate },
          groupBy: [StatsGroupEnum.CCY],
        },
      }),
    );
    store.dispatch(setShouldViewPerCurrency());
  };

  const handleExpandSum = async () => {
    await store.dispatch(
      fetchStats({
        type: StatsFetchType.EXPAND_SUM,
        request: {
          ...statsPayload,
          ...buildRequestForDeepestGeoLevel(filters),
          Date: { StartDate: startDate, EndDate: endDate },
          groupBy: [StatsGroupEnum.CCY],
        },
      }),
    );

    // Toggle the currency list visibility
    if (shouldViewCurrencies) {
      dispatch(setShouldViewCurrencies(false));
    } else {
      dispatch(setShouldViewCurrencies(true));
    }
  };

  const lastColumnBeforeCurrency = getLastColumnBeforeCurrency(
    visibleColumns.map((c) => c.name),
    (col) => col === StatsTableColumn.CCY,
  );

  // Define the footer actions for specific columns
  const footerActions: Record<string, { label: string; icon?: string; action: () => void }> = {
    [lastColumnBeforeCurrency ?? '']: {
      label: t('statisticPage.table.buttons.viewPerCurrency'),
      icon: currencyIcon,
      action: handleViewPerCurrency,
    },
    [StatsTableColumn.CCY]: {
      label: t('statisticPage.table.buttons.expandSum'),
      icon: sum,
      action: handleExpandSum,
    },
  };

  // Retrieve the appropriate footer action based on the current column
  const footerAction = footerActions[column];

  return (
    <div className={`table_footer ${getTableClass(column, 'footer', visibleColumns, isMobile)}`}>
      <div className={`table_row ${getTableClass(column, 'footer_row', visibleColumns, isMobile)}`}>
        <div
          className={`table_cell ${rowDensityClass}`}
          ref={footerAction ? menuRef : undefined}
          style={
            !footerAction
              ? borderStyle({
                  isFull: true,
                  isLastColumn,
                  isFirstColumnAfterCCY,
                })
              : undefined
          }
        >
          {footerAction ? (
            <>
              {/* Render Currency List for Currency column */}
              {column === StatsTableColumn.CCY && <CurrencyList />}
              <Button
                disabled={disableFooterSum}
                className={`button ${rowDensityClass}`}
                icon={footerAction.icon}
                iconRight={column === StatsTableColumn.CCY ? upArrowIcon : undefined}
                onClick={footerAction.action}
              >
                {footerAction.label}
              </Button>
            </>
          ) : (
            '-'
          )}
        </div>
      </div>
    </div>
  );
};

export default TableFooter;
